import { useCollections } from '@portal/core/hooks/useCollections'
import { useRPCHook } from 'components/hooks/rpc'
import { useEffect, useRef } from 'react'

type Props = {
  appId: string
  cloudUrl?: string
  enabled?: boolean
  sort?: {
    propertyPath: string
    direction: 'ascending' | 'descending'
  }
}

// Defined outside component to maintain stable reference and prevent infinite re-renders
// in useCollections dependency chain
export const DEFAULT_SORT = {
  propertyPath: 'name',
  direction: 'ascending',
} as const

export function useAppCollections({
  appId,
  cloudUrl,
  enabled = true,
  sort = DEFAULT_SORT,
}: Props) {
  const { rpcAccessToken, executeDqlQuery } = useRPCHook({
    appId,
    cloudUrl,
  })

  const hasTriggeredFetch = useRef(false)

  const { collections, isFetchingCollections, error, refetch } = useCollections(
    {
      executeDqlQuery,
      sort,
      // Don't auto-fetch - the rpcAccessToken is needed to execute DQL queries
      shouldAutoFetch: false,
    },
  )

  // Manually trigger fetch once token becomes available,
  // and only request if we haven't already
  useEffect(() => {
    if (enabled && rpcAccessToken && !hasTriggeredFetch.current) {
      hasTriggeredFetch.current = true
      refetch()
    }
  }, [enabled, rpcAccessToken, refetch])

  return {
    documents: collections,
    isFetchingCollections,
    error,
    refetch,
  }
}
